import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Scale, Search, AlertTriangle, Info, Gavel,
  CheckCircle2, Filter, FileText, ExternalLink, ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SANCTIONS, SANCTION_CATEGORIES } from '../data/sanctionsBaremes';
import { PrintButton } from '../components/PrintButton';
import { cn } from '../lib/utils';

/**
 * SanctionCard — détails repliables sur mobile.
 */
const SanctionCard = ({ s, cdata, isDelit }) => {
  const [open, setOpen] = useState(false);
  return (
    <article
      className="mc-card p-4 sm:p-5 border-l-4 hover:shadow-md transition-shadow"
      style={{ borderLeftColor: cdata.color }}
      data-testid={`sanction-${s.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:pointer-events-none w-full text-left"
        data-testid={`sanction-toggle-${s.id}`}
        aria-expanded={open}
      >
        <div className="flex flex-wrap items-start gap-2 mb-2">
          <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${cdata.color}15`, color: cdata.color }}>
            {cdata.label}
          </span>
          {isDelit ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#dc2626]/12 text-[#dc2626]">
              <Gavel className="w-3 h-3" /> Délit
            </span>
          ) : (
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-foreground/8 text-foreground/70">
              Contravention {s.classe}{s.classe?.length === 1 ? 'ᵉ classe' : ''}
            </span>
          )}
          <ChevronDown className={cn('md:hidden ml-auto w-4 h-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </div>

        <h3 className="text-base sm:text-lg font-black tracking-tight leading-snug mb-1 md:mb-3">
          {s.infraction}
        </h3>

        {/* Résumé compact visible mobile (fermé) */}
        <div className="md:hidden text-xs text-muted-foreground space-x-3 mb-1">
          <span className="font-mono font-bold text-[hsl(var(--primary))]">{s.article}</span>
          <span>·</span>
          <span>{s.amende?.split(' ')[0] || s.amende}</span>
          {s.points !== null && s.points !== undefined && (
            <>
              <span>·</span>
              <span className="font-bold text-[#b91c1c]">-{Math.abs(s.points)} pt{Math.abs(s.points) > 1 ? 's' : ''}</span>
            </>
          )}
        </div>
      </button>

      <div className={cn('md:block', open ? 'block' : 'hidden')}>
        <dl className="grid sm:grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm mt-3 md:mt-0">
          <dt className="font-bold text-foreground/65 text-xs uppercase tracking-widest pt-0.5">Article</dt>
          <dd className="font-mono text-[13px] font-bold text-[hsl(var(--primary))]">{s.article}</dd>

          <dt className="font-bold text-foreground/65 text-xs uppercase tracking-widest pt-0.5">Amende</dt>
          <dd className="text-foreground/90">{s.amende}</dd>

          <dt className="font-bold text-foreground/65 text-xs uppercase tracking-widest pt-0.5">Points retirés</dt>
          <dd>
            {s.points !== null && s.points !== undefined ? (
              <span className="inline-flex items-center gap-1 font-black text-[#b91c1c]">
                {s.points} <span className="text-xs font-bold text-foreground/60">point{Math.abs(s.points) > 1 ? 's' : ''}</span>
              </span>
            ) : (
              <span className="text-muted-foreground italic text-xs">Pas de retrait de points</span>
            )}
          </dd>

          <dt className="font-bold text-foreground/65 text-xs uppercase tracking-widest pt-0.5">Compléments</dt>
          <dd className="text-foreground/85">{s.complement}</dd>
        </dl>

        {s.note && (
          <div className="mt-3 rounded-lg p-2.5 flex items-start gap-2 text-xs leading-relaxed" style={{ background: `${cdata.color}08`, borderLeft: `2px solid ${cdata.color}` }}>
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: cdata.color }} />
            <span className="text-foreground/80">{s.note}</span>
          </div>
        )}
      </div>
    </article>
  );
};

/**
 * Page /sanctions-baremes — barème pédagogique des sanctions du Code de la route.
 *
 * - Tableau filtrable par catégorie + recherche libre
 * - Article du Code mis en évidence pour chaque infraction
 * - Disclaimer : usage pédagogique uniquement
 */
export default function SanctionsBaremes() {
  const [cat, setCat] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    document.title = 'Barème des sanctions du Code de la route — CODE 2 ROUES';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        "Barème pédagogique des principales sanctions du Code de la route 2026 : amendes, retraits de points, suspensions, articles de loi. À but d'entraînement uniquement."
      );
    }
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SANCTIONS.filter((s) => {
      if (cat !== 'all' && s.cat !== cat) return false;
      if (!q) return true;
      const haystack = `${s.infraction} ${s.article} ${s.amende} ${s.complement || ''} ${s.note || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [cat, query]);

  const counts = useMemo(() => {
    const m = { all: SANCTIONS.length };
    SANCTION_CATEGORIES.forEach((c) => {
      if (c.id !== 'all') m[c.id] = SANCTIONS.filter((s) => s.cat === c.id).length;
    });
    return m;
  }, []);

  return (
    <div className="space-y-5" data-testid="sanctions-page">
      <div className="flex items-center justify-between gap-2">
        <Link to="/" data-testid="sanctions-back">
          <Button variant="ghost" size="sm" className="-ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour à l'accueil
          </Button>
        </Link>
        <PrintButton label="Imprimer le barème" testid="sanctions-print-btn" />
      </div>

      {/* Hero */}
      <section className="mc-card relative overflow-hidden p-6 sm:p-8" data-testid="sanctions-hero">
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-20" style={{ background: 'hsl(var(--primary))' }} />
        <div className="relative">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-1 inline-flex items-center gap-1">
            <Scale className="w-3 h-3" /> Mémo pédagogique
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Barème des sanctions du Code de la route</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Les <strong>{SANCTIONS.length} infractions clés</strong> que doit connaître un candidat au permis 2 roues, classées
            par catégorie, avec l'<strong>article de loi</strong>, le montant de l'amende, le retrait
            de points et les sanctions complémentaires.
          </p>

          {/* Disclaimer */}
          <div className="mt-4 rounded-xl border border-[#f59e0b]/25 bg-[#f59e0b]/8 px-3 py-2.5 flex items-start gap-2" data-testid="sanctions-disclaimer">
            <AlertTriangle className="w-4 h-4 text-[#b45309] flex-shrink-0 mt-0.5" />
            <div className="text-[12px] leading-relaxed text-foreground/85">
              <strong className="text-[#b45309]">Usage strictement pédagogique.</strong> Ce barème est une synthèse
              à but de formation. Les sanctions réelles peuvent varier selon les circonstances, la juridiction
              ou la récidive. La référence officielle reste{' '}
              <a href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/" target="_blank" rel="noopener noreferrer" className="underline font-bold hover:text-[hsl(var(--primary))]">
                Légifrance
              </a>.
            </div>
          </div>
        </div>
      </section>

      {/* Filtres + recherche */}
      <section className="mc-card p-4 sm:p-5" data-testid="sanctions-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[hsl(var(--primary))]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Filtrer par catégorie</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="sanctions-cat-buttons">
          {SANCTION_CATEGORIES.map((c) => {
            const active = c.id === cat;
            return (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`sanctions-cat-${c.id}`}
                className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black transition-all border ${
                  active
                    ? 'text-white shadow-md scale-[1.02]'
                    : 'bg-[hsl(var(--panel))] text-foreground/75 hover:text-foreground border-border hover:border-foreground/30'
                }`}
                style={active ? { background: c.color, borderColor: c.color } : {}}
              >
                <span>{c.label}</span>
                <span className={`text-[10px] tabular-nums px-1.5 rounded-full ${active ? 'bg-white/25' : 'bg-foreground/8'}`}>
                  {counts[c.id] || 0}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une infraction, un article…"
            className="pl-9"
            data-testid="sanctions-search"
          />
        </div>
      </section>

      {/* Résultats : compteur */}
      <div className="text-xs text-muted-foreground px-1" data-testid="sanctions-count">
        {filtered.length} infraction{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''}
        {cat !== 'all' && <> dans <strong className="text-foreground">{SANCTION_CATEGORIES.find((c) => c.id === cat)?.label}</strong></>}
        {query && <> pour « <strong className="text-foreground">{query}</strong> »</>}
      </div>

      {/* Liste — cartes plutôt que tableau (responsive friendly) */}
      <section className="space-y-3" data-testid="sanctions-list">
        {filtered.length === 0 && (
          <div className="mc-card p-8 text-center text-sm text-muted-foreground" data-testid="sanctions-empty">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Aucune infraction ne correspond à ces filtres.
          </div>
        )}

        {filtered.map((s) => {
          const cdata = SANCTION_CATEGORIES.find((c) => c.id === s.cat) || SANCTION_CATEGORIES[0];
          const isDelit = s.classe === 'Délit';
          return <SanctionCard key={s.id} s={s} cdata={cdata} isDelit={isDelit} />;
        })}
      </section>

      {/* Bas de page : sources */}
      <section className="mc-card p-5 sm:p-6 mt-6" data-testid="sanctions-sources">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4 text-[#16a34a]" />
          <h2 className="text-sm font-black uppercase tracking-widest text-foreground/80">Sources officielles</h2>
        </div>
        <ul className="space-y-1.5 text-sm text-foreground/85">
          <li>
            <a href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]">
              Code de la route — Légifrance <ExternalLink className="w-3 h-3" />
            </a>
          </li>
          <li>
            <a href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006073984/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]">
              Code des assurances — Légifrance <ExternalLink className="w-3 h-3" />
            </a>
          </li>
          <li>
            <a href="https://www.securite-routiere.gouv.fr/reglementation" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]">
              Sécurité routière — Réglementation <ExternalLink className="w-3 h-3" />
            </a>
          </li>
        </ul>
        <p className="text-xs text-muted-foreground italic mt-3 leading-relaxed">
          Données vérifiées au 1<sup>er</sup> janvier 2026. Les montants des amendes forfaitaires sont susceptibles
          d'être actualisés par décret. Consulter Légifrance pour la version en vigueur.
        </p>
      </section>
    </div>
  );
}
